import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Plus } from 'lucide-react'
import { useSaveWarehouse, useWarehouses, type Warehouse, type WarehouseUpdate } from '@/api/warehouse'
import { ActionPopover } from '@/components/action-popover'
import { InlineFieldGroup, InlineFieldInput, type InlineGroupField } from '@/components/inline-field-group'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatApiErrorMessage } from '@/lib/api-error'

type AddressKey = 'address1' | 'address2' | 'city' | 'state' | 'zip'
const ADDRESS_FIELDS: ReadonlyArray<InlineGroupField<AddressKey>> = [
  { key: 'address1', label: 'Street', required: true, autoComplete: 'address-line1' },
  { key: 'address2', label: 'Suite / unit', autoComplete: 'address-line2' },
  { key: 'city', label: 'City', required: true, className: 'col-span-3', autoComplete: 'address-level2' },
  { key: 'state', label: 'State', required: true, className: 'col-span-1', autoComplete: 'address-level1' },
  { key: 'zip', label: 'ZIP', required: true, className: 'col-span-2', autoComplete: 'postal-code' },
]

type PersonKey = 'name' | 'reach'
const CONTACT_FIELDS: ReadonlyArray<InlineGroupField<PersonKey>> = [
  { key: 'name', label: 'Name', className: 'col-span-3' },
  { key: 'reach', label: 'Phone', className: 'col-span-3', inputMode: 'tel' },
]
const MANAGER_FIELDS: ReadonlyArray<InlineGroupField<PersonKey>> = [
  { key: 'name', label: 'Name', className: 'col-span-3' },
  { key: 'reach', label: 'Email', className: 'col-span-3', inputMode: 'email' },
]

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[10rem_minmax(0,1fr)] sm:items-start sm:gap-4">
      <h3 className="pt-1.5 text-xs text-muted-foreground">{label}</h3>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

function WarehouseCard({ warehouse }: { warehouse: Warehouse }) {
  const save = useSaveWarehouse()
  const [editing, setEditing] = useState<'address' | 'contact' | 'manager' | null>(null)
  const patch = (body: WarehouseUpdate, what: string, done?: () => void) =>
    save.mutate(
      { uuid: warehouse.uuid, body },
      {
        onSuccess: () => done?.(),
        onError: (error) => toast.error(formatApiErrorMessage(`${what} not saved`, error)),
      },
    )
  const editingProps = (key: 'address' | 'contact' | 'manager') => ({
    editing: editing === key,
    onEditingChange: (open: boolean) => setEditing(open ? key : null),
    pending: save.isPending,
  })
  const person = (name?: string | null, reach?: string | null) =>
    name || reach ? (
      <span className="block text-sm">
        <span className="font-medium">{name}</span>
        {reach && <span className="block text-muted-foreground">{reach}</span>}
      </span>
    ) : (
      <span className="text-sm text-muted-foreground">Add</span>
    )

  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <POInlineEditableField
              value={warehouse.name}
              ariaLabel="Warehouse name"
              pending={save.isPending}
              buttonClassName="-ml-2 text-base font-semibold"
              onConfirm={(raw) => raw.trim() && patch({ name: raw.trim() }, 'Name')}
            />
            {!warehouse.active && <StatusPill tone="neutral">Closed</StatusPill>}
          </div>
          <Link
            to="/warehouse"
            search={{ warehouse: warehouse.uuid }}
            className="text-sm text-primary hover:underline"
          >
            {warehouse.units_on_hand ? `${warehouse.units_on_hand} on hand` : 'View stock'}
          </Link>
        </div>

        <div className="space-y-2 border-t pt-3">
          <Row label="Address">
            <InlineFieldGroup
              label="Warehouse address"
              fields={ADDRESS_FIELDS}
              values={{
                address1: warehouse.address1,
                address2: warehouse.address2 ?? '',
                city: warehouse.city,
                state: warehouse.state,
                zip: warehouse.zip,
              }}
              {...editingProps('address')}
              onSave={(next) =>
                patch(
                  {
                    address1: next.address1.trim(),
                    address2: next.address2.trim(),
                    city: next.city.trim(),
                    state: next.state.trim(),
                    zip: next.zip.trim(),
                  },
                  'Address',
                  () => setEditing(null),
                )
              }
            >
              <span className="block text-sm">
                {warehouse.address1}
                {warehouse.address2 ? `, ${warehouse.address2}` : ''}
                <span className="block">
                  {warehouse.city}, {warehouse.state} {warehouse.zip}
                </span>
              </span>
            </InlineFieldGroup>
          </Row>
          <Row label="Receiving hours">
            <POInlineEditableField
              value={warehouse.receiving_hours ?? ''}
              placeholder="Add hours"
              ariaLabel="Receiving hours"
              pending={save.isPending}
              buttonClassName="-ml-2"
              onConfirm={(raw) => patch({ receiving_hours: raw.trim() }, 'Receiving hours')}
            />
          </Row>
          <Row label="Lead time (business days)">
            <POInlineEditableField
              kind="number"
              value={warehouse.lead_time_days}
              ariaLabel="Lead time in business days"
              pending={save.isPending}
              buttonClassName="-ml-2 tabular-nums"
              onConfirm={(raw) => {
                const days = Number(raw)
                if (!Number.isInteger(days) || days < 1) {
                  toast.error('Lead time is a whole number of days, at least 1')
                  return
                }
                patch({ lead_time_days: days }, 'Lead time')
              }}
            />
          </Row>
          <Row label="On-site contact">
            <InlineFieldGroup
              label="On-site contact"
              fields={CONTACT_FIELDS}
              values={{ name: warehouse.contact_name ?? '', reach: warehouse.contact_phone ?? '' }}
              {...editingProps('contact')}
              onSave={(next) =>
                patch({ contact_name: next.name, contact_phone: next.reach }, 'Contact', () => setEditing(null))
              }
            >
              {person(warehouse.contact_name, warehouse.contact_phone)}
            </InlineFieldGroup>
          </Row>
          <Row label="Manager">
            <InlineFieldGroup
              label="Warehouse manager"
              fields={MANAGER_FIELDS}
              values={{ name: warehouse.manager_name ?? '', reach: warehouse.manager_email ?? '' }}
              {...editingProps('manager')}
              onSave={(next) =>
                patch({ manager_name: next.name, manager_email: next.reach }, 'Manager', () => setEditing(null))
              }
            >
              {person(warehouse.manager_name, warehouse.manager_email)}
            </InlineFieldGroup>
          </Row>
        </div>
      </CardContent>
    </Card>
  )
}

const NEW_WAREHOUSE = { name: '', code: '', address1: '', city: '', state: '', zip: '' }

function AddWarehouse() {
  const save = useSaveWarehouse()
  const [draft, setDraft] = useState(NEW_WAREHOUSE)
  const ready = Object.values(draft).every((value) => value.trim())
  const field = (key: keyof typeof NEW_WAREHOUSE, label: string, className?: string) => (
    <InlineFieldInput
      aria-label={label}
      placeholder={label}
      value={draft[key]}
      onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
      className={className}
    />
  )
  return (
    <ActionPopover
      trigger={
        <Button variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          Add warehouse
        </Button>
      }
      title="Add warehouse"
      align="end"
      confirmLabel="Add"
      canConfirm={ready}
      pending={save.isPending}
      onConfirm={(_note, close) =>
        save.mutate(
          {
            body: {
              name: draft.name.trim(),
              code: draft.code.trim() || draft.name.trim(),
              address1: draft.address1.trim(),
              city: draft.city.trim(),
              state: draft.state.trim(),
              zip: draft.zip.trim(),
              country: 'US',
              lead_time_days: 2,
              active: true,
            },
          },
          {
            onSuccess: () => {
              setDraft(NEW_WAREHOUSE)
              close()
            },
            onError: (error) => toast.error(formatApiErrorMessage('Warehouse not added', error)),
          },
        )
      }
    >
      <div className="grid w-full gap-2">
        <div className="grid grid-cols-2 gap-2">
          {field('name', 'Name')}
          {field('code', 'Short code')}
        </div>
        {field('address1', 'Street')}
        <div className="grid grid-cols-[minmax(0,1fr)_4rem_5.5rem] gap-2">
          {field('city', 'City')}
          {field('state', 'State')}
          {field('zip', 'ZIP')}
        </div>
      </div>
    </ActionPopover>
  )
}

/** Our warehouses: where each is, when it receives, who to call, and its lead time. */
export function WarehouseLocationsPage() {
  const { data, isLoading } = useWarehouses()
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Locations</h1>
        <AddWarehouse />
      </div>
      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        data?.map((warehouse) => <WarehouseCard key={warehouse.uuid} warehouse={warehouse} />)
      )}
    </div>
  )
}
